import { Building2, BarChart3, Train, Landmark, Droplets, Briefcase, ArrowRight } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import serviceMarket from "@/assets/service-market-research.jpg";
import serviceFinancial from "@/assets/service-financial.jpg";
import serviceStrategy from "@/assets/service-strategy.jpg";
import serviceData from "@/assets/service-data.jpg";
import serviceGovernance from "@/assets/service-governance.jpg";
import serviceWater from "@/assets/service-water.jpg";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations, t } from "@/i18n/translations";

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const { language } = useLanguage();

  const icons = [Building2, BarChart3, Train, Landmark, Droplets, Briefcase];
  const images = [serviceFinancial, serviceMarket, serviceStrategy, serviceGovernance, serviceWater, serviceFinancial];

  return (
    <section id="services" className="py-24 bg-background" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-[hsl(var(--brand-red))] text-sm font-semibold tracking-widest uppercase mb-3">{t(translations.services.badge, language)}</span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">{t(translations.services.title, language)}</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            {t(translations.services.subtitle, language)}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {translations.services.items.map((service, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="group relative overflow-hidden rounded-2xl bg-card border border-border hover:border-[hsl(var(--brand-red))/0.3] transition-all duration-500 hover:shadow-[var(--shadow-premium)]"
              >
                <div className="relative h-48 overflow-hidden">
                  <img src={images[index]} alt={t(service.title, language)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--brand-navy))] via-transparent to-transparent opacity-80" />
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="p-6 space-y-4">
                  <h3 className="text-lg font-bold text-foreground group-hover:text-[hsl(var(--brand-red))] transition-colors leading-tight">
                    {t(service.title, language)}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t(service.description, language)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {service.features[language].map((f, i) => (
                      <span key={i} className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[hsl(var(--brand-navy))] to-[hsl(var(--brand-red))] p-6 sm:p-10 text-white">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-5" />
            <h3 className="text-xl sm:text-2xl font-bold mb-3">{t(translations.services.ctaTitle, language)}</h3>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto text-sm sm:text-base">
              {t(translations.services.ctaDesc, language)}
            </p>
            <a href="#contact" className="inline-flex items-center gap-2 bg-white text-[hsl(var(--brand-navy))] px-8 py-3 rounded-xl font-semibold hover:bg-white/90 transition-colors">
              {t(translations.services.ctaButton, language)}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
